import { useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, CheckCircle2, Clock } from "lucide-react";

const _fmt = (iso) => (iso ? format(parseISO(iso), "d MMM · HH:mm") : "—");

/**
 * Read-only board for admins + users flagged as Club Secretary. Every cancellation
 * / amendment notice a coach filed shows here with the full opposition contact
 * pack the digest email carries — status pending until the cadence cron picks
 * it up (or realtime fired inline).
 */
export function SecretaryTab() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/secretary/notices");
      setRows(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load notices");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const visible = (rows || []).filter(r => filter === "all" || r.status === filter);
  const pendingCount = (rows || []).filter(r => r.status === "pending").length;

  return (
    <div className="space-y-4">
      <div className="card-tactical rounded-md p-5">
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <div className="font-display font-bold text-2xl tracking-tight flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#83B68B]" /> Secretary notices
            </div>
            <p className="text-sm opacity-60 mt-1">
              Every cancellation / amendment a coach filed. The Club Secretary uses this to notify the opposition. Cadence editable via Settings → <code>secretary_cadence</code>.
            </p>
          </div>
          <button onClick={load} data-testid="secretary-refresh" className="text-[10px] tracking-widest uppercase px-3 py-1.5 border border-border hover:border-[#83B68B] hover:text-[#83B68B] rounded transition-colors">{busy ? "Loading…" : "Refresh"}</button>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {["all", "pending", "sent"].map(v => (
          <button
            key={v}
            onClick={() => setFilter(v)}
            data-testid={`secretary-filter-${v}`}
            className={`text-[10px] tracking-widest uppercase px-3 py-1.5 rounded border transition-colors ${filter === v ? "border-[#83B68B] bg-[#83B68B]/10 text-[#83B68B]" : "border-border hover:border-white/40"}`}
          >
            {v} {v === "pending" && pendingCount > 0 && <span className="ml-1 text-amber-300">· {pendingCount}</span>}
          </button>
        ))}
      </div>

      <div className="card-tactical rounded-md overflow-hidden overflow-x-auto">
        {!rows ? (
          <div className="p-8 text-sm opacity-60 text-center">Loading…</div>
        ) : visible.length === 0 ? (
          <div className="p-8 text-sm opacity-60 text-center">No notices to show.</div>
        ) : (
          <table className="w-full text-sm" data-testid="secretary-notices-table">
            <thead className="bg-muted text-[10px] tracking-widest uppercase opacity-60">
              <tr>
                <th className="text-left px-3 py-3">Filed</th>
                <th className="text-left px-3 py-3">Kind</th>
                <th className="text-left px-3 py-3">EYFC team</th>
                <th className="text-left px-3 py-3">Opposition</th>
                <th className="text-left px-3 py-3">Fixture</th>
                <th className="text-left px-3 py-3">Kick-off</th>
                <th className="text-left px-3 py-3">Opp. secretary</th>
                <th className="text-left px-3 py-3">Reason</th>
                <th className="text-left px-3 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {visible.map(n => (
                <tr key={n.id} className="border-t border-border" data-testid={`secretary-row-${n.id}`}>
                  <td className="px-3 py-3 whitespace-nowrap opacity-80">{_fmt(n.created_at)}</td>
                  <td className="px-3 py-3">
                    <span className={`text-[10px] tracking-widest uppercase px-1.5 py-0.5 rounded ${n.kind === "cancel" ? "bg-red-500/20 text-red-400" : "bg-amber-500/20 text-amber-300"}`}>{n.kind}</span>
                  </td>
                  <td className="px-3 py-3 font-medium">{n.team_name || "—"}</td>
                  <td className="px-3 py-3">{n.opponent || "—"}</td>
                  <td className="px-3 py-3 opacity-80 whitespace-nowrap">{n.date_of_fixture || "—"}</td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    {n.original_kickoff || "—"}
                    {n.kind === "amend" && n.new_kickoff && <div className="text-[10px] text-[#83B68B]">→ {n.new_kickoff}</div>}
                  </td>
                  <td className="px-3 py-3 text-xs">
                    <div className="font-medium">{n.opp_secretary_name || "—"}</div>
                    {n.opp_secretary_email && <a href={`mailto:${n.opp_secretary_email}`} className="opacity-70 hover:text-[#83B68B]">{n.opp_secretary_email}</a>}
                  </td>
                  <td className="px-3 py-3 text-xs opacity-80 max-w-xs">{n.reason || "—"}</td>
                  <td className="px-3 py-3">
                    {n.status === "sent"
                      ? <span className="inline-flex items-center gap-1 text-[#83B68B] text-[10px] tracking-widest uppercase"><CheckCircle2 className="w-3.5 h-3.5" /> Sent {n.sent_at ? `· ${_fmt(n.sent_at)}` : ""}</span>
                      : <span className="inline-flex items-center gap-1 text-amber-300 text-[10px] tracking-widest uppercase"><Clock className="w-3.5 h-3.5" /> Pending</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
